/**
 * NotificationBell — In-app notification bell for Patchwise.
 *
 * Polls UITL central notification API every 30s for unread count.
 * Click opens popover with recent notifications.
 * Shares JWT with UITL (same auth system).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  IconButton, Badge, Popover, Box, Typography, Button,
  CircularProgress, ListItemButton, Stack,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const UITL_API = (process.env.REACT_APP_UITL_API_URL || 'https://app.uitl.ai') + '/api/v1/notifications';
const POLL_INTERVAL = 30_000;

interface NotifItem {
  id: number;
  level: string;
  title: string;
  body?: string;
  action_url?: string;
  read_at?: string | null;
  created_at?: string;
}

function getHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token');
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function levelIcon(level: string) {
  switch (level) {
    case 'warning': return <WarningAmberIcon fontSize="small" sx={{ color: 'warning.main' }} />;
    case 'error':
    case 'critical': return <ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />;
    default: return <InfoOutlinedIcon fontSize="small" sx={{ color: 'info.main' }} />;
  }
}

const NotificationBell: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<NotifItem[]>([]);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchCount = useCallback(async () => {
    if (!user) return;
    try {
      const resp = await fetch(`${UITL_API}/unread-count`, { headers: getHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        setUnreadCount(data.unread_count ?? data.count ?? 0);
      }
    } catch { /* silent */ }
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const resp = await fetch(`${UITL_API}/?limit=15`, { headers: getHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        setNotifications(Array.isArray(data) ? data : data.notifications ?? []);
      }
    } catch { /* silent */ }
    setLoading(false);
  }, [user]);

  const markRead = useCallback(async (id: number) => {
    try {
      await fetch(`${UITL_API}/${id}/read`, { method: 'POST', headers: getHeaders() });
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch { /* silent */ }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await fetch(`${UITL_API}/mark-all-read`, { method: 'POST', headers: getHeaders() });
      setNotifications((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnreadCount(0);
    } catch { /* silent */ }
  }, []);

  // Poll unread count
  useEffect(() => {
    if (!user) return;
    fetchCount();
    pollRef.current = setInterval(fetchCount, POLL_INTERVAL);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [user, fetchCount]);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    fetchNotifications();
  };

  const handleClick = async (notif: NotifItem) => {
    if (!notif.read_at) await markRead(notif.id);
    setAnchorEl(null);
    if (notif.action_url) navigate(notif.action_url);
  };

  if (!user) return null;

  return (
    <>
      <IconButton
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        onClick={handleOpen}
        sx={{ ml: 1 }}
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon fontSize="small" />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 360, maxHeight: 480 } } }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Notifications</Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={markAllRead} startIcon={<CheckCircleOutlineIcon />}>
              Mark all read
            </Button>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No notifications</Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 380, overflow: 'auto' }}>
            {notifications.map((notif) => (
              <ListItemButton
                key={notif.id}
                onClick={() => handleClick(notif)}
                sx={{
                  px: 2, py: 1.5,
                  bgcolor: notif.read_at ? 'transparent' : 'action.hover',
                  borderBottom: '1px solid', borderColor: 'divider',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: '100%' }}>
                  <Box sx={{ mt: 0.3 }}>{levelIcon(notif.level)}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: notif.read_at ? 400 : 600, fontSize: 13, lineHeight: 1.3 }}>
                      {notif.title}
                    </Typography>
                    {notif.body && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {notif.body.length > 80 ? notif.body.slice(0, 80) + '...' : notif.body}
                      </Typography>
                    )}
                    {notif.created_at && (
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                        {timeAgo(notif.created_at)}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </ListItemButton>
            ))}
          </Box>
        )}
      </Popover>
    </>
  );
};

export default NotificationBell;
